import React, { useLayoutEffect, useRef, useState } from 'react';
import ProfaseModal from '../../../commons/ProfaseModal';
import { FaFileImport } from '@react-icons/all-files/fa/FaFileImport';
import PageForm, { FormField } from '../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../store';
import { createEmpresaEnGrupo, importNuevaEmpresaConfigPdf } from '../grupo-empresarial.reducer';
import { fileHasPdfMagic, extractImportWarningFromBody, parseImportConfigBody, str } from './empresa-config-import.shared';

export interface EmpresaCrearFormValues {
  razonSocial: string;
  rfc: string;
}

function makeEmptyForm(): EmpresaCrearFormValues {
  return { razonSocial: '', rfc: '' };
}

const fields: FormField<EmpresaCrearFormValues>[] = [
  { name: 'razonSocial', label: 'Razón Social', required: true, placeholder: 'Razón social' },
  { name: 'rfc', label: 'RFC', required: true, placeholder: 'RFC' },
];

const ACCENT = '#6366f1';

export interface EmpresaCrearModalProps {
  open: boolean;
  forceHidden?: boolean;
  grupoId: string | undefined;
  onClose: () => void;
  onCreated?: (empresaId: number) => void;
}

export default function EmpresaCrearModal({
  open,
  forceHidden = false,
  grupoId,
  onClose,
  onCreated,
}: EmpresaCrearModalProps) {
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.grupoEmpresarial.updating);

  const [form, setForm] = useState<EmpresaCrearFormValues>(() => makeEmptyForm());
  const [errors, setErrors] = useState<Partial<Record<keyof EmpresaCrearFormValues, string>>>({});
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [importPdfBusy, setImportPdfBusy] = useState(false);
  const [importPdfError, setImportPdfError] = useState<string | null>(null);
  const [importPdfWarning, setImportPdfWarning] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    setForm(makeEmptyForm());
    setErrors({});
    setImportPdfError(null);
    setImportPdfWarning(null);
  }, [open]);

  const handleFieldChange = (name: keyof EmpresaCrearFormValues, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handlePdfImportClick = () => {
    setImportPdfError(null);
    setImportPdfWarning(null);
    pdfInputRef.current?.click();
  };

  const handlePdfFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !grupoId || grupoId === 'new') return;
    setImportPdfError(null);
    setImportPdfWarning(null);
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setImportPdfError('Solo se permiten archivos .pdf');
      return;
    }
    if (file.type && file.type !== 'application/pdf') {
      setImportPdfError('El archivo debe ser PDF (application/pdf).');
      return;
    }
    if (!(await fileHasPdfMagic(file))) {
      setImportPdfError('El archivo no es un PDF válido (falta la cabecera %PDF).');
      return;
    }
    let pdfBytes: ArrayBuffer;
    try {
      pdfBytes = await file.arrayBuffer();
    } catch {
      setImportPdfError('No se pudo leer el archivo.');
      return;
    }
    setImportPdfBusy(true);
    try {
      const res = await dispatch(importNuevaEmpresaConfigPdf({ grupoId, pdfBytes })).unwrap();
      const raw = (res as any)?.data !== undefined ? (res as any).data : res;
      const parsed = parseImportConfigBody(raw);
      const info = parsed?.info;
      if (!info) {
        setImportPdfWarning(null);
        setImportPdfError('La respuesta no incluye datos reconocibles del PDF.');
        return;
      }
      const rfc = str(info.rfc).trim();
      const razonSocial = str(info.razonSocial).trim();
      if (!rfc && !razonSocial) {
        setImportPdfWarning(null);
        setImportPdfError('El PDF no devolvió RFC ni razón social.');
        return;
      }
      setForm(prev => ({
        ...prev,
        rfc: rfc || prev.rfc,
        razonSocial: razonSocial || prev.razonSocial,
      }));
      setErrors({});
      setImportPdfError(null);
      setImportPdfWarning(extractImportWarningFromBody(raw));
    } catch (err: any) {
      const raw = err?.data ?? err;
      const parsed = parseImportConfigBody(raw);
      const info = parsed?.info;
      if (info) {
        const rfc = str(info.rfc).trim();
        const razonSocial = str(info.razonSocial).trim();
        if (rfc || razonSocial) {
          setForm(prev => ({
            ...prev,
            rfc: rfc || prev.rfc,
            razonSocial: razonSocial || prev.razonSocial,
          }));
          setErrors({});
          setImportPdfError(null);
          setImportPdfWarning(extractImportWarningFromBody(raw));
          return;
        }
      }
      setImportPdfWarning(null);
      const msg =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : 'No se pudo importar el PDF.';
      setImportPdfError(msg);
    } finally {
      setImportPdfBusy(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!grupoId || grupoId === 'new') return;
    const errs: Partial<Record<keyof EmpresaCrearFormValues, string>> = {};
    if (!form.razonSocial.trim()) errs.razonSocial = 'La razón social es requerida';
    if (!form.rfc.trim()) errs.rfc = 'El RFC es requerido';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    try {
      const res = await dispatch(
        createEmpresaEnGrupo({
          grupoId,
          empresa: {
            razonSocial: form.razonSocial.trim(),
            rfc: form.rfc.trim(),
          },
        }),
      ).unwrap();
      const raw = res.data as { id?: number | string };
      const idNum = raw?.id != null ? Number(raw.id) : NaN;
      if (!Number.isNaN(idNum)) {
        if (onCreated) onCreated(idNum);
        else onClose();
      }
    } catch {
      /* 403 u otro error: interceptor / UI global */
    }
  };

  if (!open) return null;

  if (forceHidden) return null;

  const savingOrImport = updating || importPdfBusy;

  return (
    <ProfaseModal
      open
      titleId="empresa-crear-modal-title"
      onBackdropMouseDown={e => {
        if (e.target === e.currentTarget && !savingOrImport) onClose();
      }}
    >
        <h2 id="empresa-crear-modal-title" className="profase-modal-title">
          Agregar empresa
        </h2>
        <p className="profase-modal-description">
          Indique RFC y razón social, o importe un PDF para rellenarlos automáticamente.
        </p>
        {importPdfError && (
          <p className="profase-form-alert-error">{importPdfError}</p>
        )}
        {importPdfWarning && (
          <p className="profase-form-alert-warning">{importPdfWarning}</p>
        )}
        <PageForm
          embedded
          fields={fields}
          values={form}
          errors={errors}
          isNew
          saving={savingOrImport}
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
          onCancel={onClose}
          accentColor={ACCENT}
          submitLabel="Crear empresa"
          cancelLabel="Cancelar"
          actionSlotAfterCancel={
            <button
              type="button"
              className="profase-form-btn-secondary"
              style={{ opacity: savingOrImport ? 0.75 : 1, cursor: savingOrImport ? 'not-allowed' : 'pointer' }}
              onClick={handlePdfImportClick}
              disabled={savingOrImport}
            >
              <FaFileImport style={{ marginRight: 6 }} />
              {importPdfBusy ? 'Importando…' : 'Importar'}
            </button>
          }
        />
        <input
          ref={pdfInputRef}
          type="file"
          accept="application/pdf,.pdf"
          style={{ display: 'none' }}
          aria-hidden
          tabIndex={-1}
          onChange={handlePdfFileChange}
        />
    </ProfaseModal>
  );
}
